import React , { useEffect, useState, useContext, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Spin, Icon, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { parse } from 'svgson';
import { ComplexEditingContext } from '../../../context/complexEditingContext';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { PickerContainer, ColorButton } from '../../../artboard/Toolbar/styles';
import ColorPicker from '../../../artboard/ColorPicker';
import { getSvgColors, updateSvgColor, updateBorderRectangle,
   updateSvgLineWeight, updateDoubleLineDistance, updateLineStyles, updateBorderCircle} from '../../../utils/svgUtils';
import { SvgContextToolbar, LineWeightSliderWrapper, LineWeightLabel, LineWeightSlider, LineWeightInput,
   BorderButton, LineStylePopover, ButtonSeparator, LineWeightButton, LineStyleButtonsWrapper,
   LineStyleOptionWrapper, LineStyleGroup, LineStyleOption, CheckIcon, LineStyleButton } from '../../../artboard/Toolbar/styles';
import { IMAGE_TOOLBAR } from '../../../artboard/Toolbar/translationValues';

const NAMESPACES = ['IMAGE_TOOLBAR'];
const HEIGHT_REGEX = /[^.\d]/g;
const REGEX_TRANSFORM = /\((.*?)\)/g;

const SvgEditorReusable = (props) => {
  const { uuid, element, editable, selected, isFromCreativeBlock } = props;
  const { t } = useTranslation(NAMESPACES);
  const { updateComplexElementDetails, updateElementStyles } = useContext(PageElementsContext);
  const imageRef = useContext(ComplexEditingContext);
  const elementRef = useRef(false);

  const [picker, setPicker] = useState(false);
  const [colorObj, setColor] = useState(null);
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderRadius, setBorderRadius] = useState(0);

  const [lineWeight, setLineWeight] = useState(0);
  const [lineStyle, setLineStyle] = useState('');
  const [lineDistance, setLineDistance] = useState(0);

  const [showBorderWidthPopover, setBorderWidthPopover] = useState(false);
  const [showBorderRadiusPopover, setBorderRadiusPopover] = useState(false);
  const [showLineWeightPopover, setLineWeightPopover] = useState(false);
  const [showLineStylePopover, setLineStylePopover] = useState(false);
  const [showLineDistancePopover, setLineDistancePopover] = useState(false);
  const [forceRefresh, toggleForceRefresh] = useState(false);
  const uiType = element.uiType;

  const toggleBorderWidthPopover = useCallback(() => {
    setBorderWidthPopover(previousValue => !previousValue);
  }, []);

  const toggleBorderRadiusPopover = useCallback(() => {
    setBorderRadiusPopover(previousValue => !previousValue);
  }, []);

  const toggleLineWeightPopover = useCallback(() => {
    setLineWeightPopover(previousValue => !previousValue);
  }, []);

  const toggleLineStylePopover = useCallback(() => {
    setLineStylePopover(previousValue => !previousValue);
  }, []);

  const toggleLineDistancePopover = useCallback(() => {
    setLineDistancePopover(previousValue => !previousValue);
  }, []);

  useEffect(() => {
    (async () => {
      if (imageRef.current.get(element.uuid) === undefined && !isFromCreativeBlock) { // Static block new element added
        const svgJSON = await parse(element.url);
        imageRef.current.set(element.uuid, { json: svgJSON, svg: element.url })
      }
    })();
  }, []);

  let svgJson, minValue = 0;

  if (imageRef?.current) {
    svgJson = imageRef.current.get(element.uuid)?.json;
    if (svgJson?.attributes && element.style) {
      const svgWidth = isFromCreativeBlock ? element.style?.width : svgJson.attributes?.viewBox?.split(" ")[2];
      const svgHeight = isFromCreativeBlock ? element.style?.height : svgJson.attributes?.viewBox?.split(" ")[3];
      minValue = svgWidth > svgHeight ? svgHeight : svgWidth;
    }
  }

  const handleColors = (value, index) => {
    const pickerStatus = (colorObj && colorObj.index !== index) || true;
    index = pickerStatus ? index : null;
    value = { ...value, index };
    setPicker(pickerStatus);
    setColor(value);
  };

  const onColorPick = async color => {
    let opacity = color.rgb.a.toFixed(2);
    let newColor = { ...colorObj, newColor: color.hex };
    const updateColor = { ...newColor, oldColor: color.hex };
    const { element: updatedElement, parsedString, svgString } = await updateSvgColor(
      colorObj.oldColor,
      color.hex,
      {
        elementDetails: {
          value: svgJson
        }
      },
      opacity,
    );
    imageRef.current.set(element.uuid, { json: parsedString, svg: svgString });
    updateComplexElementDetails(uuid, svgString, 'svg-value');
    setColor(updateColor);
  };

  const colors = svgJson ? getSvgColors(svgJson) : [];

  const handleBorderWidthChange = useCallback(updatedBorderWidth => {
    const { element: updatedElement, parsedString, stringSVG } = updateBorderRectangle(
      updatedBorderWidth,
      borderRadius,
      {
        elementDetails: {
          style: {},
          value: imageRef.current.get(element.uuid)?.json
        }
      },
      isFromCreativeBlock
    );
    updateComplexElementDetails(uuid, stringSVG, 'svg-value');
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setBorderWidth(updatedBorderWidth);
  }, [borderRadius])

  const handleCircleBorderWidthChange = useCallback(updatedBorderWidth => {
    const { element: updatedElement, parsedString, stringSVG } = updateBorderCircle(
      updatedBorderWidth,
      {
        elementDetails: {
          style: {},
          value: imageRef.current.get(element.uuid)?.json
        }
      },
      isFromCreativeBlock
    );
    updateComplexElementDetails(uuid, stringSVG, 'svg-value');
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setBorderWidth(updatedBorderWidth);
  }, []);

  const handleBorderRadiusChange = useCallback(updatedBorderRadius => {
    const { element: updatedElement, parsedString, stringSVG } = updateBorderRectangle(
      borderWidth,
      updatedBorderRadius,
      {
        elementDetails: {
          style: {},
          value: imageRef.current.get(element.uuid)?.json
        }
      },
      isFromCreativeBlock
    );
    updateComplexElementDetails(uuid, stringSVG, 'svg-value');
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setBorderRadius(updatedBorderRadius);
  }, [borderWidth]);

  const handleWeightChange = useCallback(weight => {
    const currentSVGJson = imageRef.current.get(element.uuid)?.json;
    const { element: updatedElement, parsedString, stringSVG } = updateSvgLineWeight(weight, {
      elementDetails: {
        style: {
          width: currentSVGJson.attributes.width,
          height: currentSVGJson.attributes.height,
          viewBox: currentSVGJson?.attributes?.viewBox
        },
        uiType: uiType,
        value: currentSVGJson,
        staticTableBlock: !isFromCreativeBlock
      }
    }, 'style');
    updateComplexElementDetails(uuid, { url: stringSVG, style: updatedElement.elementDetails.style, staticTableBlock: !isFromCreativeBlock }, 'svg-value-with-styles');
    let updatedElementDetails = {
      height: `${weight}px`,
      uuid: uuid,
    };
    updateElementStyles(updatedElementDetails);
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setLineWeight(weight);
  }, [uiType]);

  const handleLineDistanceChange = useCallback(distance => {
    const currentSVGJson = imageRef.current.get(element.uuid)?.json;
    const { element: updatedElement, parsedString, stringSVG } = updateDoubleLineDistance(distance, {
      elementDetails: {
        style: {
          width: currentSVGJson.attributes.width,
          height: currentSVGJson.attributes.height
        },
        uiType: uiType,
        value: currentSVGJson
      }
    }, 'style');
    updateComplexElementDetails(uuid, { url: stringSVG, style: updatedElement.elementDetails.style, staticTableBlock: !isFromCreativeBlock }, 'svg-value-with-styles');
    let updatedElementDetails = {
      height: updatedElement.elementDetails.style.height,
      uuid: uuid,
    };
    updateElementStyles(updatedElementDetails);
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setLineDistance(distance);
  }, [uiType]);

  const handleStyleChange = useCallback(e => {
    const lineStyle = e.target.value;
    const currentSVGJson = imageRef.current.get(element.uuid)?.json;
    const { element: updatedElement, parsedString, stringSVG } = updateLineStyles(lineStyle, {
     elementDetails: {
       style: {
         width: currentSVGJson.attributes.width,
         height: currentSVGJson.attributes.height,
         viewBox: currentSVGJson?.attributes?.viewBox
       },
       uiType: uiType,
       value: currentSVGJson,
       staticTableBlock: !isFromCreativeBlock
     }
    }, 'style');
    updateComplexElementDetails(uuid, stringSVG, 'svg-value');
    imageRef.current.set(element.uuid, { json: parsedString, svg: stringSVG });
    setLineStyle(lineStyle);
  }, [uiType]);

  const isRectangle = uiType === 'rectangle';
  const isCircle = uiType === 'circle';
  const isDoubleLine = uiType === 'doubleLine';
  const isLine = uiType === 'line' || isDoubleLine;

  useEffect(() => {
    if (elementRef.current) {
      const resetImageJSON = async e => {
        const { element: newElement } = e.detail;
        const parsedJSON = await parse(newElement.url);
        imageRef.current.set(uuid, { json: parsedJSON, svg: newElement.url });
        toggleForceRefresh(previousValue => !previousValue);
      }
      elementRef.current.addEventListener('handleEditorUndoRedo', resetImageJSON);
      return () => {
        elementRef.current.removeEventListener('handleEditorUndoRedo', resetImageJSON);
      }
    }
  }, []);

  useEffect(() => {
    const currentSVGJson = imageRef.current.get(element.uuid)?.json;
    if (uiType === 'rectangle' && currentSVGJson) {
      setBorderWidth(parseInt(currentSVGJson.attributes['data-border-width']) || 0);
      setBorderRadius(parseInt(currentSVGJson.attributes['data-border-radius']) || 0);
    } if (uiType === 'circle' && currentSVGJson) {
      setBorderWidth(parseInt(currentSVGJson.children[0].attributes['stroke-width']) || 0);
    } else if (uiType === 'doubleLine' && currentSVGJson) {
      const lineHeight = currentSVGJson.children[0].children[0].attributes['height'];
      const strokeWidth = currentSVGJson.children[0].children[0].attributes['stroke-width'];
      let initialLineDistance = lineHeight - strokeWidth;
      setLineWeight(strokeWidth);
      setLineDistance(initialLineDistance);
    } else if (uiType === 'line' && currentSVGJson) {
      const strokeWidth = parseInt(`${currentSVGJson.children[0].attributes.transform}`.match(REGEX_TRANSFORM)[0].replaceAll('(', '').replaceAll(')', ''));
      setLineWeight(strokeWidth);
      setLineStyle(currentSVGJson.attributes['data-linetype']);
    }
  }, [uiType, selected]);

  const setPreserveAspectRatio = (url) => {
    let parser = new DOMParser();
    let svgDoc = parser.parseFromString(url, "image/svg+xml");
    let svgElement = svgDoc.querySelector('svg');

    svgElement.setAttribute('preserveAspectRatio', 'none');

    // Convert the modified SVG element back into a string
    let updatedSVGString = new XMLSerializer().serializeToString(svgElement);
    return updatedSVGString;
  }
  
  if (!element.url) {
    return <Spin />;
  }

  return (
    <>
      <div id={`img-${element.uuid}`} ref={elementRef} dangerouslySetInnerHTML={{__html: !isFromCreativeBlock && element.uiType === 'line' ? setPreserveAspectRatio(element.url) : element.url }}/>
      {editable && selected && svgJson && ReactDOM.createPortal(
        (<SvgContextToolbar>
          {(isRectangle || isCircle)  && (
            <Popover
              content={
                  <LineWeightSliderWrapper>
                    <LineWeightLabel>
                      <p>{t(IMAGE_TOOLBAR.BORDER_WIDTH_LABEL.key, IMAGE_TOOLBAR.BORDER_WIDTH_LABEL.value)}</p>
                      <LineWeightSlider
                        min={0}
                        max={borderRadius !== 0 ? Math.min(borderRadius, minValue / 2) : minValue / 2}
                        onChange={isCircle ? handleCircleBorderWidthChange : handleBorderWidthChange}
                        value={typeof borderWidth !== 'undefined' ? borderWidth : 1}
                      />
                      <LineWeightInput
                        min={0}
                        max={borderRadius !== 0 ? Math.min(borderRadius, minValue / 2) : minValue / 2}
                        style={{ marginLeft: 16 }}
                        value={borderWidth}
                        onChange={isCircle ? handleCircleBorderWidthChange : handleBorderWidthChange}
                      />
                    </LineWeightLabel>
                  </LineWeightSliderWrapper>
              }
              trigger="click"
              placement="bottomLeft"
              visible={showBorderWidthPopover}
              onVisibleChange={setBorderWidthPopover}
            >
              <BorderButton onClick={toggleBorderWidthPopover}>
                <Icon type={"border"} style={{ fontSize: 18 }} />
              </BorderButton>
            </Popover>
          )}
          {isRectangle && (
            <Popover
              content={(
                <LineWeightSliderWrapper>
                  <LineWeightLabel>
                    <p>{t(IMAGE_TOOLBAR.BORDER_RADIUS_LABEL.key, IMAGE_TOOLBAR.BORDER_RADIUS_LABEL.value)}</p>
                    <LineWeightSlider
                      min={0}
                      max={minValue / 2}
                      onChange={handleBorderRadiusChange}
                      value={typeof borderRadius !== 'undefined' ? borderRadius : 1}
                    />
                    <LineWeightInput
                      min={0}
                      max={minValue / 2}
                      style={{ marginLeft: 16 }}
                      value={borderRadius}
                      onChange={handleBorderRadiusChange}
                    />
                  </LineWeightLabel>
                </LineWeightSliderWrapper>
              )}
              trigger="click"
              placement="bottomLeft"
              visible={showBorderRadiusPopover}
              onVisibleChange={setBorderRadiusPopover}
            >
              <BorderButton onClick={toggleBorderRadiusPopover}>
                <Icon type={"radius-upleft"} style={{ fontSize: 18 }} />
              </BorderButton>
            </Popover>
          )}
          {colors &&
            colors.map((data, index) => {
              const backgroundColor = data.hexColor || data.newColor;
              const value = {
                uuid,
                oldColor: data.oldColor,
                newColor: backgroundColor,
              };
              return (
                <ColorButton
                  key={index}
                  style={{ backgroundColor }}
                  onClick={() => handleColors(value, index)}
                />
              );
          })}
          {isLine && (
            <>
              <ButtonSeparator />
              <Popover
                content={(
                  <LineWeightSliderWrapper>
                    <LineWeightLabel>
                      <p>{t(IMAGE_TOOLBAR.LINE_WEIGHT_LABEL.key, IMAGE_TOOLBAR.LINE_WEIGHT_LABEL.value)}</p>
                      <LineWeightSlider
                        min={1}
                        max={100}
                        onChange={handleWeightChange}
                        value={typeof lineWeight !== 'undefined' ? lineWeight : 1}
                      />
                      <LineWeightInput
                        min={1}
                        max={100}
                        style={{ marginLeft: 16 }}
                        value={lineWeight}
                        onChange={handleWeightChange}
                      />
                    </LineWeightLabel>
                  </LineWeightSliderWrapper>
                )}
                trigger="click"
                placement="bottomLeft"
                visible={showLineWeightPopover}
                onVisibleChange={setLineWeightPopover}
              >
                <LineWeightButton onClick={toggleLineWeightPopover}>
                  <span className="lineheightwarapper">
                    <span aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                        <rect width="18" height="1.5" x="3" y="4" fill="#00000" rx=".75" />
                        <rect width="18" height="3" x="3" y="8.5" fill="#00000" rx="1" />
                        <rect width="18" height="5.5" x="3" y="14.5" fill="#00000" rx="1" />
                      </svg>
                    </span>
                  </span>
                </LineWeightButton>
              </Popover>
              {isDoubleLine && (
                <Popover
                  content={(
                    <LineWeightSliderWrapper>
                      <LineWeightLabel>
                        <p>{t(IMAGE_TOOLBAR.LINE_DISTANCE_LABEL.key, IMAGE_TOOLBAR.LINE_DISTANCE_LABEL.value)}</p>
                        <LineWeightSlider
                          min={1}
                          max={100}
                          onChange={handleLineDistanceChange}
                          value={typeof lineDistance !== 'undefined' ? lineDistance : 1}
                        />
                        <LineWeightInput
                          min={1}
                          max={100}
                          style={{ marginLeft: 16 }}
                          value={lineDistance}
                          onChange={handleLineDistanceChange}
                        />
                      </LineWeightLabel>
                    </LineWeightSliderWrapper>
                  )}
                  trigger="click"
                  placement="bottomLeft"
                  visible={showLineDistancePopover}
                  onVisibleChange={setLineDistancePopover}
                >
                  <LineWeightButton onClick={toggleLineDistancePopover}>
                    <svg
                      viewBox="24 24 896 896"
                      focusable="false"
                      data-icon="column-height"
                      width="19"
                      height="23"
                      fill="#000000"
                      aria-hidden="true"
                    >
                      <path d="M840 836H184c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h656c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8zm0-724H184c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h656c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8zM610.8 378c6 0 9.4-7 5.7-11.7L515.7 238.7a7.14 7.14 0 00-11.3 0L403.6 366.3a7.23 7.23 0 005.7 11.7H476v268h-62.8c-6 0-9.4 7-5.7 11.7l100.8 127.5c2.9 3.7 8.5 3.7 11.3 0l100.8-127.5c3.7-4.7.4-11.7-5.7-11.7H548V378h62.8z" />
                    </svg>
                  </LineWeightButton>
                </Popover>
              )}
              {!isDoubleLine && (
                <LineStylePopover
                  content={(
                    <LineStyleButtonsWrapper>
                      <LineStyleGroup defaultValue={lineStyle ? lineStyle : 'square'} onChange={handleStyleChange}>
                        <LineStyleOption value="square">
                          <LineStyleOptionWrapper>
                            <svg className="svg-lines" viewBox="0 0 48 24">
                              <line
                                x1="2"
                                y1="50%"
                                x2="46"
                                y2="50%"
                                stroke="#000000"
                                strokeLinecap="square"
                                strokeWidth="4"
                                strokeDasharray=""
                              />
                            </svg>
                            <CheckIcon type="check" />
                          </LineStyleOptionWrapper>
                        </LineStyleOption>
                        <LineStyleOption value="round">
                          <LineStyleOptionWrapper>
                            <svg className="svg-lines" viewBox="0 0 48 24">
                              <line
                                x1="2"
                                y1="50%"
                                x2="46"
                                y2="50%"
                                stroke="#000000"
                                strokeLinecap="round"
                                strokeWidth="4"
                                strokeDasharray=""
                              />
                            </svg>
                            <CheckIcon type="check" />
                          </LineStyleOptionWrapper>
                        </LineStyleOption>
                        <LineStyleOption value="dash">
                          <LineStyleOptionWrapper>
                            <svg className="svg-lines" viewBox="0 0 44 24">
                              <line
                                x1="2"
                                y1="50%"
                                x2="46"
                                y2="50%"
                                stroke="#000000"
                                strokeLinecap="round"
                                strokeWidth="4"
                                strokeDasharray="4,8"
                              />
                            </svg>
                            <CheckIcon type="check" />
                          </LineStyleOptionWrapper>
                        </LineStyleOption>
                        <LineStyleOption value="dot">
                          <LineStyleOptionWrapper>
                            <svg className="svg-lines" viewBox="0 0 44 24">
                              <line
                                x1="2"
                                y1="50%"
                                x2="46"
                                y2="50%"
                                stroke="#000000"
                                strokeLinecap="round"
                                strokeWidth="4"
                                strokeDasharray="0,8"
                              />
                            </svg>
                            <CheckIcon type="check" />
                          </LineStyleOptionWrapper>
                        </LineStyleOption>
                      </LineStyleGroup>
                    </LineStyleButtonsWrapper>
                  )}
                  trigger="click"
                  placement="bottomLeft"
                  visible={showLineStylePopover}
                  onVisibleChange={setLineStylePopover}
                >
                  <LineStyleButton onClick={toggleLineStylePopover}>
                    <span className="linestylewrapper">
                      <span aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                          <path
                            fill="#000000"
                            fillRule="evenodd"
                            d="M3 4.75c0-.414.302-.75.675-.75h16.65c.373 0 .675.336.675.75s-.302.75-.675.75H3.675C3.302 5.5 3 5.164 3 4.75zM7.75 12a.75.75 0 0 1-.75.75H3.75a.75.75 0 0 1 0-1.5H7a.75.75 0 0 1 .75.75zm6.4 0a.75.75 0 0 1-.75.75h-3.3a.75.75 0 0 1 0-1.5h3.3a.75.75 0 0 1 .75.75zm6.35 0a.75.75 0 0 1-.75.75H16.5a.75.75 0 0 1 0-1.5h3.25a.75.75 0 0 1 .75.75zM4.55 19.5a.75.75 0 0 1-.75.75h-.05a.75.75 0 0 1 0-1.5h.05a.75.75 0 0 1 .75.75zm3.2 0a.75.75 0 0 1-.75.75h-.1a.75.75 0 0 1 0-1.5H7a.75.75 0 0 1 .75.75zm3.2 0a.75.75 0 0 1-.75.75h-.1a.75.75 0 0 1 0-1.5h.1a.75.75 0 0 1 .75.75zm3.2 0a.75.75 0 0 1-.75.75h-.1a.75.75 0 0 1 0-1.5h.1a.75.75 0 0 1 .75.75zm3.2 0a.75.75 0 0 1-.75.75h-.1a.75.75 0 0 1 0-1.5h.1a.75.75 0 0 1 .75.75zm3.15 0a.75.75 0 0 1-.75.75h-.05a.75.75 0 0 1 0-1.5h.05a.75.75 0 0 1 .75.75z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </span>
                  </LineStyleButton>
                </LineStylePopover>
              )}
            </>
          )}
          {picker && (
            <PickerContainer style={{height: '366px'}}>
              <ColorPicker
                onChange={onColorPick}
                onBlur={() => setPicker(false)}
                color={colorObj.newColor}
                qlEditor={true}
              />
            </PickerContainer>
          )}
        </SvgContextToolbar>),
        document.getElementById('document-editor-toolbar'),
      )}
    </>
  )
}

export default SvgEditorReusable;